import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { TrendingUp, TrendingDown, Eye, FileText, CheckCircle2, Truck } from "lucide-react";

const METRICS = [
  { label: "Visits", value: 64, trend: "4%", positive: true, icon: Eye },
  { label: "Quotes sent", value: 37, trend: "9%", positive: true, icon: FileText },
  { label: "Deals won", value: 19, trend: "8%", positive: true, icon: CheckCircle2 },
  { label: "Dispatches", value: 42, trend: "10%", positive: true, icon: Truck },
];

const CHART_LINE =
  "M0,118 C35,112 55,98 85,92 C115,86 135,102 165,98 C195,94 215,58 245,52 C275,46 295,64 325,58 C355,52 375,26 415,18 C435,13 455,11 480,8";

const CountUp = ({ value }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 1.4,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value]);

  return <span ref={ref}>{display.toLocaleString("en-IN")}</span>;
};

export default function DashboardMockup() {
  return (
    <div className="relative" data-testid="hero-dashboard-mockup">
      <div className="bg-white rounded-3xl border border-slate-200/70 shadow-2xl shadow-slate-900/10 p-6 sm:p-8">
        <div className="flex items-center justify-between gap-4">
          <h3 className="font-display font-extrabold text-xl sm:text-2xl tracking-tight">Business at a glance</h3>
          <span
            className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-600 text-xs font-bold px-3 py-1.5 rounded-full shrink-0"
            data-testid="dashboard-live-badge"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            Live
          </span>
        </div>

        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-5">
          {METRICS.map((m) => (
            <div key={m.label} data-testid={`metric-${m.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-500">
                <m.icon className="w-3.5 h-3.5 text-slate-400" />
                {m.label}
              </p>
              <p className="mt-1.5 font-display font-extrabold text-3xl sm:text-4xl text-brand-ink">
                <CountUp value={m.value} />
              </p>
              <p className={`mt-1 inline-flex items-center gap-1 text-xs font-bold ${m.positive ? "text-emerald-600" : "text-red-500"}`}>
                {m.positive ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                {m.trend}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 border-t border-slate-100 pt-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-bold text-brand-ink">Activity overview</p>
            <p className="text-xs text-slate-400">Last 7 days</p>
          </div>
          <svg viewBox="0 0 480 140" className="w-full h-32 sm:h-36" data-testid="dashboard-sparkline" preserveAspectRatio="none">
            <defs>
              <linearGradient id="yesaiArea" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#006AD8" stopOpacity="0.14" />
                <stop offset="100%" stopColor="#006AD8" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={`${CHART_LINE} L480,140 L0,140 Z`} fill="url(#yesaiArea)" />
            <motion.path
              d={CHART_LINE}
              fill="none"
              stroke="#006AD8"
              strokeWidth="2.5"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.6, ease: "easeInOut" }}
            />
          </svg>
          <div className="mt-3 flex items-center justify-between">
            <p className="flex items-center gap-2 text-xs font-semibold text-slate-600">
              <span className="w-2 h-2 rounded-full bg-brand-red" />
              Sales activity
            </p>
            <p className="text-[11px] text-slate-400">Sales · Visits · Dispatch</p>
          </div>
        </div>
      </div>
    </div>
  );
}
